/**
 * ZonePlacementPanel — live placement-settings experimentation for the planner.
 *
 * Pick a placeable area, toggle heading / pattern / lanes / coupling / pitch /
 * progression / margin / caps, and watch its slot count + peak concurrency (and
 * the field total) recompute instantly in the scene + CapacityPanel. Save writes
 * the layout columns back to `public.areas` via `updateHangar`.
 *
 * Edits flow through `eventPlannerStore.updateZoneSettings`, which rebuilds the
 * zone slot grids and re-derives placements (deterministic re-place of the same
 * prefix). Runtime-only until Save — mirrors the MotionSettingsPanel pattern.
 */
import { useEffect, useState } from 'react'
import { Save, X } from 'lucide-react'
import { sceneRotationToCompass, compassToSceneRotation } from '../engine/heading'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { useQueryClient } from '@tanstack/react-query'
import { updateHangar, fetchHangar } from '@/lib/api/hangars'
import { hangarKeys } from '@/hooks/useHangars'
import type { HangarUpdate } from '@/types/hangars'
import type { PlacementConfig, PatternParams } from '@/types/area-placement'
import { AREA_PARKING_MODES, AREA_PARKING_MODE_LABELS } from '@/types/area-placement'
import type { AreaParkingMode } from '@/types/area-placement'
import { FormationPicker } from '@/components/placement/FormationPicker'
import { DensityControl } from '@/components/placement/DensityControl'
import {
  formationFields,
  deriveFormationKey,
  densityFromFields,
  pitchForDensity,
  footprintRowsForDensity,
  type FormationKey,
  type Density,
} from '@/lib/placement/formations'
import {
  useEventPlannerStore,
  useZoneFit,
  selectCapacity,
  selectPeakConcurrent,
  selectZoneSlotCount,
  selectZonePeakConcurrent,
} from '../store/eventPlannerStore'
import type { ZoneGeometry } from '../engine/types'
import type { ZonePricingOverride } from '../data/eventPricing'

interface Props {
  onClose?: () => void
  /** Scene origin [lng, lat] — converts a moved area's center back to lat/lng on save. */
  center?: [number, number] | null
}

/** Normalize any degree value into [0, 360). */
const norm360 = (deg: number): number => ((deg % 360) + 360) % 360

const field = 'w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-xs text-white/90'
const label = 'text-[10px] font-semibold uppercase tracking-wide text-white/40'
const sectionTitle = 'text-[10px] font-bold uppercase tracking-widest text-white/30'

const numOrUndef = (v: string): number | undefined => {
  const n = Number(v)
  return v.trim() !== '' && Number.isFinite(n) ? n : undefined
}

export function ZonePlacementPanel({ onClose, center }: Props) {
  const zones = useEventPlannerStore((s) => s.zones)
  const updateZoneSettings = useEventPlannerStore((s) => s.updateZoneSettings)
  const clearZoneDirty = useEventPlannerStore((s) => s.clearZoneDirty)
  const zoneDirty = useEventPlannerStore((s) => s.zoneDirty)
  const totalCapacity = useEventPlannerStore(selectCapacity)
  const totalPeak = useEventPlannerStore(selectPeakConcurrent)

  const areaId = useEventPlannerStore((s) => s.selectedZoneId)
  const setAreaId = useEventPlannerStore((s) => s.setSelectedZoneId)
  const overrides = useEventPlannerStore((s) => s.zonePricingOverrides)
  const setOverride = useEventPlannerStore((s) => s.setZonePricingOverride)

  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Step size (ft) for the Move nudge pad.
  const [moveStep, setMoveStep] = useState(25)

  // Default to (and recover to) the first zone once geometry loads. Selection is
  // shared store state so the on-map labels + focus camera stay in sync.
  useEffect(() => {
    if (zones.length === 0) {
      if (areaId !== null) setAreaId(null)
    } else if (!zones.some((z) => z.areaId === areaId)) {
      setAreaId(zones[0].areaId)
    }
  }, [zones, areaId, setAreaId])

  const zone = zones.find((z) => z.areaId === areaId) ?? null
  const slotCount = useEventPlannerStore((s) => (areaId ? selectZoneSlotCount(s, areaId) : 0))
  const zonePeak = useEventPlannerStore((s) => (areaId ? selectZonePeakConcurrent(s, areaId) : 0))
  const dirty = areaId ? zoneDirty.has(areaId) : false

  const patch = (p: Partial<ZoneGeometry>) => {
    if (areaId) updateZoneSettings(areaId, p)
  }

  const override: ZonePricingOverride = (areaId ? overrides[areaId] : undefined) ?? {}
  const patchOverride = (p: Partial<ZonePricingOverride>) => {
    if (!areaId) return
    const next = { ...override, ...p }
    const empty =
      !next.isHangar && next.rampPerNight == null && next.overnightPerNight == null && next.hangarPerNight == null
    setOverride(areaId, empty ? null : next)
  }
  const overrideNum = (v: string): number | null => {
    const n = Number(v)
    return v.trim() !== '' && Number.isFinite(n) ? n : null
  }

  // Display/edit the heading as a TRUE compass bearing (0 = north). Stored as the
  // scene Y-rotation; convert only at this UI boundary. See engine/heading.ts.
  const compassHeading = zone ? Math.round(sceneRotationToCompass(zone.placementHeadingDeg)) : 0
  const setCompass = (deg: number) => patch({ placementHeadingDeg: compassToSceneRotation(deg) })

  // Area heading = compass bearing of the zone rectangle itself (rotationDeg).
  // Rotating it turns the WHOLE area — the footprint and the parked aircraft —
  // by applying the same scene-rotation delta to placementHeadingDeg (which stays
  // independently adjustable under Orientation as a fine plane-facing offset).
  const areaCompass = zone ? Math.round(sceneRotationToCompass(zone.rotationDeg)) : 0
  const setAreaHeading = (deg: number) => {
    if (!zone) return
    const nextRotation = compassToSceneRotation(norm360(deg))
    const delta = nextRotation - zone.rotationDeg
    patch({
      rotationDeg: nextRotation,
      placementHeadingDeg: norm360(zone.placementHeadingDeg + delta),
    })
  }

  // Move the area by nudging its scene-feet center (no drag). Scene axes: +X east,
  // +Z south — so N = −Z, S = +Z, E = +X, W = −X.
  const nudge = (dx: number, dz: number) => {
    if (!zone) return
    patch({ center: [zone.center[0] + dx, zone.center[1] + dz] })
  }

  // Formation = the named recipe over pattern/progression/lanes/coupling; Density
  // = the folded pitch + tight-pack knob. Both derive from the live zone fields.
  const formationKey: FormationKey | 'custom' = zone ? deriveFormationKey(zone) : 'single_row'
  const density: Density = densityFromFields(zone?.pitchMode, zone?.footprintRows)
  const setFormation = (key: FormationKey) => patch(formationFields(key))
  const setDensity = (d: Density) =>
    patch({ pitchMode: pitchForDensity(d), footprintRows: footprintRowsForDensity(d) })

  const onSave = async () => {
    if (!zone) return
    setSaving(true)
    setError(null)
    try {
      // Merge the pattern_params (tight-pack toggle + nested ratio) into the
      // existing placement_config so we don't clobber sibling keys (layout,
      // access, buffer, lane_b_exit_id) authored elsewhere.
      const current = await fetchHangar(zone.areaId)
      const prevConfig = (current.placement_config as PlacementConfig | null) ?? {}
      const patternParams: PatternParams = {
        ...(prevConfig.pattern_params ?? {}),
        footprint_rows: zone.footprintRows ?? false,
      }
      if (zone.tailWidthRatio !== undefined) patternParams.tail_width_ratio = zone.tailWidthRatio
      const placement_config: PlacementConfig = { ...prevConfig, pattern_params: patternParams }

      // Persist the scalar layout columns + placement_config. Size caps live in
      // the `eligibility` JSONB alongside dwell/clients/enforcement — writing them
      // here would clobber those siblings, so caps stay live-only experiment
      // knobs (authored in the building editor's eligibility form).
      // Area position: convert the (possibly moved) scene-feet center back to a
      // lng/lat for persistence. Only set when the scene origin is known.
      let position: { latitude: number; longitude: number } | undefined
      if (center) {
        const [lng, lat] = CoordinateService.scenePositionToLngLat(
          [zone.center[0], 0, zone.center[1]],
          center
        )
        position = { latitude: lat, longitude: lng }
      }

      await updateHangar(zone.areaId, {
        // Persist as a TRUE compass bearing (matches loadZoneGeometry + the
        // building-editor authoring field). In-memory it's the scene rotation.
        placement_heading: Math.round(sceneRotationToCompass(zone.placementHeadingDeg)),
        pattern: zone.pattern,
        progression: zone.progression,
        lanes: zone.lanes,
        coupling: zone.coupling,
        pitch_mode: zone.pitchMode,
        safety_margin_ft: zone.safetyMarginFt,
        fill_priority: zone.fillPriority,
        parking_mode: zone.parkingMode ?? null,
        tow_min_wingspan_ft: zone.towMinWingspanFt ?? null,
        // Area footprint geometry (move / resize / rotate). `rotation` is the scene
        // Y-rotation — the same convention loadZoneGeometry reads back from `rotation`.
        exterior_width: Math.round(zone.width),
        exterior_depth: Math.round(zone.depth),
        rotation: Math.round(zone.rotationDeg),
        ...position,
        placement_config,
      } as HangarUpdate)
      clearZoneDirty(zone.areaId)
      // Refresh hangar lists + this area's detail so other views (building editor,
      // settings) reflect the saved geometry. Deliberately NOT hangarKeys.all: that
      // includes the '3d' scene query, and re-fetching it re-streams every building
      // into the scene (the stream effect adds without clearing → duplicate meshes).
      // The live footprint already tracks the edits via useEventZoneSync.
      queryClient.invalidateQueries({ queryKey: hangarKeys.lists() })
      queryClient.invalidateQueries({ queryKey: hangarKeys.detail(zone.areaId) })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pointer-events-auto absolute right-4 top-4 bottom-4 z-[8] flex w-[360px] flex-col gap-3 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/85 p-3 text-white backdrop-blur-md">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold">Zone Placement</div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Zone selector */}
      <div className="space-y-1">
        <div className={label}>Area</div>
        <select
          className={field}
          value={areaId ?? ''}
          onChange={(e) => setAreaId(e.target.value)}
          disabled={zones.length === 0}
        >
          {zones.length === 0 && <option value="">No placeable areas</option>}
          {zones.map((z) => (
            <option key={z.areaId} value={z.areaId}>
              {z.name}
            </option>
          ))}
        </select>
      </div>

      {zone && (
        <>
          {/* Live capacity readout */}
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Zone slots" value={slotCount} />
            <Stat label="Zone peak" value={zonePeak} />
            <Stat label="Field slots" value={totalCapacity} />
            <Stat label="Field peak" value={totalPeak} />
          </div>

          {/* What fits here — and what to do when it doesn't */}
          <ZoneFitNote areaId={areaId} />

          {/* Area — move / resize / rotate the placeable area itself (control-only,
              no drag). Edits update the rendered footprint + slot grid live. */}
          <div className="space-y-2">
            <div className={sectionTitle}>Area</div>

            {/* Size */}
            <div className="grid grid-cols-2 gap-2">
              <Labeled label="Width (ft)">
                <input
                  type="number"
                  min={1}
                  step={10}
                  className={field}
                  value={Math.round(zone.width)}
                  onChange={(e) => patch({ width: Math.max(1, Number(e.target.value) || 0) })}
                />
              </Labeled>
              <Labeled label="Depth (ft)">
                <input
                  type="number"
                  min={1}
                  step={10}
                  className={field}
                  value={Math.round(zone.depth)}
                  onChange={(e) => patch({ depth: Math.max(1, Number(e.target.value) || 0) })}
                />
              </Labeled>
            </div>

            {/* Heading — rotates the whole area (footprint + parked aircraft) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span
                  className={label}
                  title="True compass bearing of the area (0° = north). Rotates the footprint and the parked aircraft together."
                >
                  Area heading
                </span>
                <span className="text-xs text-white/70 tabular-nums">{areaCompass}°</span>
              </div>
              <div className="flex items-center gap-1.5">
                <HeadingStep delta={-5} onClick={() => setAreaHeading(areaCompass - 5)} />
                <HeadingStep delta={-1} onClick={() => setAreaHeading(areaCompass - 1)} />
                <input
                  type="range"
                  min={0}
                  max={359}
                  step={1}
                  value={areaCompass}
                  onChange={(e) => setAreaHeading(Number(e.target.value))}
                  className="min-w-0 flex-1"
                />
                <HeadingStep delta={1} onClick={() => setAreaHeading(areaCompass + 1)} />
                <HeadingStep delta={5} onClick={() => setAreaHeading(areaCompass + 5)} />
              </div>
            </div>

            {/* Move — nudge the area position by a fixed step (compass directions) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className={label}>Move</span>
                <div className="flex items-center gap-1">
                  {[5, 25].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setMoveStep(s)}
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                        moveStep === s
                          ? 'bg-sky-500/30 text-sky-100'
                          : 'bg-black/40 text-white/50 hover:text-white'
                      }`}
                    >
                      {s}ft
                    </button>
                  ))}
                </div>
              </div>
              <div className="mx-auto grid w-max grid-cols-3 gap-1">
                <div />
                <NudgeButton label="N" onClick={() => nudge(0, -moveStep)} />
                <div />
                <NudgeButton label="W" onClick={() => nudge(-moveStep, 0)} />
                <div className="flex items-center justify-center text-[9px] tabular-nums text-white/30">
                  {moveStep}ft
                </div>
                <NudgeButton label="E" onClick={() => nudge(moveStep, 0)} />
                <div />
                <NudgeButton label="S" onClick={() => nudge(0, moveStep)} />
                <div />
              </div>
            </div>
          </div>

          {/* Formation — one tap sets pattern + progression + lanes + coupling */}
          <div className="space-y-2">
            <div className={sectionTitle}>Formation</div>
            <FormationPicker tone="dark" value={formationKey} onChange={setFormation} />
            {formationKey === 'custom' && (
              <p className="text-[10px] text-amber-300/70">
                Custom layout — fine-tune the raw fields under Advanced.
              </p>
            )}
          </div>

          {/* Orientation */}
          <div className="space-y-2">
            <div className={sectionTitle}>Orientation</div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className={label} title="True compass bearing the aircraft nose points (0° = north)">
                  Placement heading
                </span>
                <span className="text-xs text-white/70 tabular-nums">{compassHeading}°</span>
              </div>
              <div className="flex items-center gap-1.5">
                <HeadingStep delta={-5} onClick={() => setCompass(compassHeading - 5)} />
                <HeadingStep delta={-1} onClick={() => setCompass(compassHeading - 1)} />
                <input
                  type="range"
                  min={0}
                  max={359}
                  step={1}
                  value={compassHeading}
                  onChange={(e) => setCompass(Number(e.target.value))}
                  className="min-w-0 flex-1"
                />
                <HeadingStep delta={1} onClick={() => setCompass(compassHeading + 1)} />
                <HeadingStep delta={5} onClick={() => setCompass(compassHeading + 5)} />
              </div>
            </div>
          </div>

          {/* Spacing */}
          <div className="space-y-2">
            <div className={sectionTitle}>Spacing</div>
            <Labeled label="Mode">
              <DensityControl tone="dark" value={density} onChange={setDensity} />
            </Labeled>
            {density === 'comfortable' && (
              <Labeled label="Slot width (ft)">
                <input
                  type="number"
                  min={0}
                  step={1}
                  className={field}
                  placeholder="largest aircraft"
                  value={zone.maxWingspanFt ?? ''}
                  onChange={(e) => patch({ maxWingspanFt: numOrUndef(e.target.value) })}
                />
                <p className="mt-1 text-[10px] text-white/30">
                  Fixed slot box for every aircraft. Empty = sized to the largest type offered.
                </p>
              </Labeled>
            )}
            <Labeled label="Safety margin (ft)">
              <input
                type="number"
                min={0}
                step={1}
                className={field}
                value={zone.safetyMarginFt}
                onChange={(e) => patch({ safetyMarginFt: Math.max(0, Number(e.target.value) || 0) })}
              />
            </Labeled>
          </div>

          {/* Advanced — operational + raw overrides */}
          <details className="space-y-2">
            <summary className="cursor-pointer list-none text-[10px] font-bold uppercase tracking-widest text-white/30 hover:text-white/50">
              Advanced
            </summary>
            <div className="mt-2 space-y-3">
              <Labeled label="Fill priority">
                <input
                  type="number"
                  className={field}
                  value={zone.fillPriority}
                  onChange={(e) => patch({ fillPriority: Number(e.target.value) || 0 })}
                />
              </Labeled>

              {zone.coupling === 'nested' && (
                <Labeled label="Nest ratio (tail ÷ wing)">
                  <input
                    type="number"
                    min={0.2}
                    max={1}
                    step={0.05}
                    className={field}
                    value={zone.tailWidthRatio ?? 0.6}
                    onChange={(e) => patch({ tailWidthRatio: Number(e.target.value) || 0.6 })}
                  />
                </Labeled>
              )}

              {/* Parking / towing policy */}
              <div className="space-y-2">
                <div className={sectionTitle}>Parking / towing</div>
                <Labeled label="Parking mode">
                  <select
                    className={field}
                    value={zone.parkingMode ?? 'both'}
                    onChange={(e) => patch({ parkingMode: e.target.value as AreaParkingMode })}
                  >
                    {AREA_PARKING_MODES.map((m) => (
                      <option key={m} value={m}>
                        {AREA_PARKING_MODE_LABELS[m]}
                      </option>
                    ))}
                  </select>
                </Labeled>
                {(zone.parkingMode ?? 'both') === 'both' && (
                  <Labeled label="Tow if wingspan ≥ (ft)">
                    <input
                      type="number"
                      className={field}
                      placeholder="default"
                      value={zone.towMinWingspanFt ?? ''}
                      onChange={(e) => patch({ towMinWingspanFt: numOrUndef(e.target.value) })}
                    />
                  </Labeled>
                )}
              </div>

              {/* Eligibility size caps (also the uniform slot box) */}
              <div className="space-y-2">
                <div className={sectionTitle}>Size caps</div>
                <p className="text-[10px] text-white/30">Affects live capacity only — not saved here.</p>
                <div className="grid grid-cols-2 gap-2">
                  <Labeled label="Max wingspan (ft)">
                    <input
                      type="number"
                      className={field}
                      placeholder="—"
                      value={zone.maxWingspanFt ?? ''}
                      onChange={(e) => patch({ maxWingspanFt: numOrUndef(e.target.value) })}
                    />
                  </Labeled>
                  <Labeled label="Max length (ft)">
                    <input
                      type="number"
                      className={field}
                      placeholder="—"
                      value={zone.maxLengthFt ?? ''}
                      onChange={(e) => patch({ maxLengthFt: numOrUndef(e.target.value) })}
                    />
                  </Labeled>
                </div>
              </div>
            </div>
          </details>

          {/* Per-zone pricing override (revenue only; not persisted to the DB) */}
          <div className="space-y-2">
            <div className={sectionTitle}>Pricing override</div>
            <p className="text-[10px] text-white/30">Overrides the event rate card for this zone — affects revenue stats only.</p>
            <label className="flex items-center gap-2 text-xs text-white/80">
              <input
                type="checkbox"
                checked={override.isHangar ?? false}
                onChange={(e) => patchOverride({ isHangar: e.target.checked })}
              />
              <span>
                Bill as hangar
                <span className="ml-1 text-white/40">— hangar fee instead of ramp + overnight</span>
              </span>
            </label>
            {override.isHangar ? (
              <Labeled label="Hangar / night ($)">
                <input
                  type="number"
                  className={field}
                  placeholder="tier default"
                  value={override.hangarPerNight ?? ''}
                  onChange={(e) => patchOverride({ hangarPerNight: overrideNum(e.target.value) })}
                />
              </Labeled>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Labeled label="Ramp / night ($)">
                  <input
                    type="number"
                    className={field}
                    placeholder="tier default"
                    value={override.rampPerNight ?? ''}
                    onChange={(e) => patchOverride({ rampPerNight: overrideNum(e.target.value) })}
                  />
                </Labeled>
                <Labeled label="Overnight / night ($)">
                  <input
                    type="number"
                    className={field}
                    placeholder="tier default"
                    value={override.overnightPerNight ?? ''}
                    onChange={(e) => patchOverride({ overnightPerNight: overrideNum(e.target.value) })}
                  />
                </Labeled>
              </div>
            )}
          </div>

          {error && <div className="text-xs text-red-400">{error}</div>}

          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || saving}
            className="mt-auto mb-12 flex items-center justify-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/20 px-3 py-2 text-sm font-semibold text-sky-100 transition-colors hover:bg-sky-500/30 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : dirty ? 'Save settings' : 'Saved'}
          </button>
        </>
      )}
    </div>
  )
}

/**
 * Capacity advice for the selected area: how big a hull it can park, how many of
 * the offered fleet are too large (they spill to larger areas), and a resize
 * nudge when the area fits nothing or some aircraft fit nowhere.
 */
function ZoneFitNote({ areaId }: { areaId: string | null }) {
  const fit = useZoneFit(areaId)
  if (!areaId) return null

  if (fit.slotCount === 0) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-2 text-[11px] text-red-200">
        Area too small to fit any aircraft. Increase its width or depth, or lower the slot width
        / safety margin.
      </div>
    )
  }

  return (
    <div className="space-y-1.5 rounded-lg border border-white/10 bg-black/30 px-2.5 py-2 text-[11px]">
      <div className="text-white/70">
        Largest hull this area parks:{' '}
        <span className="font-semibold tabular-nums text-sky-300">{Math.round(fit.maxSlotFt)} ft</span>
      </div>
      {fit.oversizeCount > 0 && (
        <div className="text-amber-200/90">
          {fit.oversizeCount} offered aircraft are larger and spill to bigger areas. Raise the slot
          width (Fixed slot) or enlarge this area to keep them here.
        </div>
      )}
      {fit.overflow > 0 && (
        <div className="text-red-200">
          {fit.overflow} aircraft couldn’t be placed in any area — enlarge an area or add capacity.
        </div>
      )}
      {fit.oversizeCount === 0 && fit.overflow === 0 && (
        <div className="text-emerald-300/80">Fits every aircraft in the offered fleet.</div>
      )}
    </div>
  )
}

function Stat({ label: l, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-wide text-white/40">{l}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  )
}

/** Compact ±N° nudge button for fine-tuning the placement heading. */
function HeadingStep({ delta, onClick }: { delta: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${delta > 0 ? '+' : ''}${delta}°`}
      className="shrink-0 rounded border border-white/10 bg-black/40 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white/70 hover:bg-white/10 hover:text-white"
    >
      {delta > 0 ? `+${delta}` : delta}
    </button>
  )
}

/** Compact direction button for the area Move nudge pad. */
function NudgeButton({ label: l, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Move ${l}`}
      className="h-7 w-7 rounded border border-white/10 bg-black/40 text-xs font-semibold text-white/70 hover:bg-white/10 hover:text-white"
    >
      {l}
    </button>
  )
}

function Labeled({ label: l, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className={label}>{l}</div>
      {children}
    </div>
  )
}
